/**
 * Storage driver interface + shared helpers.
 *
 * Every concrete backend (Local, Firebase, Convex) implements the StorageDriver
 * contract so `useStorage` can delegate reads/writes without knowing which backend
 * is active. This extraction is Session 1 of the Convex migration — see
 * `CONVEX_MIGRATION.md` §16 for the full rationale.
 *
 * No behavior change from pre-extraction. Firebase RTDB still holds authoritative
 * state; LocalDriver still powers the demo build; ConvexDriver will land in Session 2.
 */

/**
 * @typedef {(
 *   'chars'|'food'|'diets'|'diseases'|'systems'|
 *   'foodTypes'|'locations'|'globalSettings'|'spellLibrary'|
 *   'groupTravelLog'|'currentLocation'|'messages'|
 *   'journal'|'dmJournal'|'npcLibrary'|'journalSettings'|
 *   'combat'|'combatSnapshots'|'audit'|'shopRuntime'|'lootRuntime'
 * )} CollectionPath
 */

/**
 * @typedef {(
 *   CollectionPath|
 *   'auditSessionMeta'|'auditMeta'|'auditRecent'|
 *   'messageLiveTail'|'messagePanelHistory'|'messageUnreadCounts'|
 *   'journalRecent'|'journalPage'|'journalEntry'|
 *   'dmJournalRecent'|'dmJournalPage'|'dmJournalEntry'
 * )} SubscriptionPath
 */

/**
 * @typedef {'checking'|'local'|'connected'|'disconnected'|'error'} ConnectionStatus
 */

/**
 * @typedef {Object} AuditEventPayload
 * @property {string} [category]
 * @property {string} [action]
 * @property {string} [summary]
 * @property {string} [charId]
 * @property {string} [sessionId]
 * @property {number} [ts]
 * @property {any} [details]
 * @property {any} [revert]
 * @property {any} [actor]
 * @property {boolean} [isDemo]
 * @property {string} [importance]
 */

/**
 * @typedef {Object} AuditSessionPayload
 * @property {string} [id]
 * @property {number} [startedAt]
 * @property {number} [endedAt]
 * @property {string} [summary]
 */

/**
 * @typedef {Object} StorageDriver
 * @property {'local'|'convex'|'routed'} kind
 *   Identifies the driver implementation.
 * @property {((path: SubscriptionPath, args: any) => void) | undefined} [setSubscriptionArgs]
 *   Optional dynamic arguments for virtual subscription paths such as journal
 *   pages. Drivers that do not need path args may omit it.
 * @property {(path: SubscriptionPath, handler: (value: any) => void) => () => void} subscribe
 *   Register a handler for a top-level collection or virtual subscription path.
 *   Called synchronously with the current cached value and on every subsequent
 *   change. Returns an unsubscribe function.
 * @property {(path: CollectionPath, value: any, options?: { expectedUpdatedAt?: number, expectedVersion?: number, fullSnapshot?: boolean, allowLegacyJournalSet?: boolean }) => void} save
 *   Set the whole collection at `path`. For journal paths, `fullSnapshot: true`
 *   explicitly marks the value as delete-authoritative; bounded row/page values
 *   should omit it or pass `false`.
 * @property {((args: { entryId?: string, entry?: any } | any) => Promise<any>) | undefined} [upsertJournalEntry]
 *   Row-level player journal upsert. Targets the current authenticated journal
 *   runtime scope on Convex and the local journal array in demo mode.
 * @property {((args: { entryId?: string, id?: string }) => Promise<any>) | undefined} [deleteJournalEntry]
 *   Row-level player journal delete with backend tombstone semantics where
 *   supported.
 * @property {((args: { entryId: string, campaignId?: string | null }) => Promise<any>) | undefined} [setJournalEntryCampaignScope]
 *   Campaign tag: move a player journal entry between the universal "Lore"
 *   scope (campaignId null) and a campaign scope. Convex enforces authority
 *   (entry owner or campaign DM) and tombstones the source copy; the local
 *   driver stamps/clears campaignId on the flat journal array.
 * @property {((args: { entryId?: string, entry?: any } | any) => Promise<any>) | undefined} [upsertDmJournalEntry]
 *   Row-level DM journal upsert. Targets the current authenticated journal
 *   runtime scope on Convex and the local DM journal array in demo mode.
 * @property {((args: { entryId?: string, id?: string }) => Promise<any>) | undefined} [deleteDmJournalEntry]
 *   Row-level DM journal delete with backend tombstone semantics where
 *   supported.
 * @property {((args: { entryId?: string, entry?: any } | any) => Promise<any>) | undefined} [upsertGlobalLoreEntry]
 *   Campaign-neutral lore upsert. Convex always targets the global lore scope,
 *   even when the signed-in user has an active campaign.
 * @property {((args: { entryId?: string, id?: string }) => Promise<any>) | undefined} [deleteGlobalLoreEntry]
 *   Campaign-neutral lore delete. Convex always targets the global lore scope,
 *   even when the signed-in user has an active campaign.
 * @property {((args: { lookups?: Array<{ category: string, name?: string, nameKey?: string }>, category?: string, name?: string, nameKey?: string, limit?: number }) => Promise<any>) | undefined} [lookupJournalEntriesByName]
 *   Row-level player journal exact category/name lookup for duplicate detection.
 * @property {((args: { lookups?: Array<{ category: string, name?: string, nameKey?: string }>, category?: string, name?: string, nameKey?: string, limit?: number }) => Promise<any>) | undefined} [lookupDmJournalEntriesByName]
 *   Row-level DM journal exact category/name lookup for duplicate detection.
 * @property {((args: { query?: string, categories?: string[], npcRefs?: string[], questNpcNames?: string[], includeSessionMeta?: boolean, limit?: number, categoryLimit?: number, sessionLimit?: number, questLimit?: number }) => Promise<any>) | undefined} [discoverJournalEntries]
 *   Point-in-time player journal discovery query for bounded autocomplete,
 *   fuzzy NPC reference lookup, quest associations, and session metadata.
 *   This must remain imperative; do not expose it as a live subscription path.
 * @property {((args: { query?: string, categories?: string[], npcRefs?: string[], questNpcNames?: string[], includeSessionMeta?: boolean, limit?: number, categoryLimit?: number, sessionLimit?: number, questLimit?: number }) => Promise<any>) | undefined} [discoverDmJournalEntries]
 *   Point-in-time DM journal discovery query with DM authorization checks on
 *   backends that support private DM rows.
 * @property {((args: { messageId: string }) => Promise<any>) | undefined} [markStatusMessageSeen]
 *   Mark a status/cinematic message as seen in the current runtime scope.
 * @property {((args: { messageId: string }) => Promise<any>) | undefined} [dismissStatusMessage]
 *   Dismiss a status/cinematic message in the current runtime scope.
 * @property {((args: { charId: string, messageIds: string[] }) => Promise<any>) | undefined} [markDmChatRead]
 *   Mark DM chat messages read for one character in the current runtime scope.
 * @property {((args: { message: any }) => Promise<any>) | undefined} [sendStatusMessage]
 *   Send a status/cinematic message in the current runtime scope.
 * @property {((args: { charId: string, message: any }) => Promise<any>) | undefined} [sendDmChatMessage]
 *   Send a DM chat message for one character in the current runtime scope.
 * @property {((args: { charId: string, messageId: string }) => Promise<any>) | undefined} [deleteDmChatMessage]
 *   Tombstone a DM chat message for one character in the current runtime scope.
 * @property {(patch: Record<string, any>) => Promise<void>} updateMultiple
 *   Atomic multi-path update. Keys are slash-separated paths like
 *   `"audit/events/xyz"`. Flushes any pending per-path saves first so a trailing
 *   `save('chars', ...)` can't overwrite this patch's chars/* keys after it lands.
 * @property {(event: AuditEventPayload) => Promise<string>} pushAuditEvent
 *   Append a single audit event; resolves to the generated event id.
 * @property {(session: AuditSessionPayload) => Promise<string>} pushAuditSession
 *   Append a session boundary; resolves to the generated session id.
 * @property {((args: { charId: string, updates: { currentHp?: number, tempHp?: number, currentMana?: number }, expected?: { currentHp?: number, tempHp?: number, currentMana?: number }, surface?: string }) => Promise<any>) | undefined} [patchCharacterVitals]
 *   Session 6.5 authority seam for narrow character resource writes. When
 *   implemented by the active backend, owns HP/temp HP/mana updates and audit.
 *   Missing on backends that still use the legacy client-composed save path.
 * @property {((args: { charId: string, instanceKey: string, patch: Record<string, any>, historyEntry?: Record<string, any> }) => Promise<any>) | undefined} [patchCharacterSystemInstance]
 *   Slice 8 authority seam for narrow DM system-instance writes. When
 *   implemented by the active backend, patches one applied system entry inside
 *   one character and owns the audit event.
 * @property {((args: { charId: string, updates: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, expected?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, audit?: Record<string, any> }) => Promise<any>) | undefined} [patchCharacterSystemState]
 *   Slice 8 authority seam for DM system-scoped character writes. When
 *   implemented by the active backend, patches only systemList, removedSystems,
 *   and glowingSystems on one character and owns the audit event.
 * @property {((args: { charId: string, systemUpdates?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, systemExpected?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, vitalsUpdates?: { currentHp?: number, tempHp?: number, currentMana?: number }, vitalsExpected?: { currentHp?: number, tempHp?: number, currentMana?: number }, audit?: Record<string, any> }) => Promise<any>) | undefined} [patchCharacterSystemStateAndVitals]
 *   Slice 8 authority seam for DM system-rule writes that touch only the
 *   system-state fields and character vitals fields. Owns a single audit event.
 * @property {((args: { charId: string, systemUpdates?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, systemExpected?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, resourceUpdates?: { currentHp?: number, tempHp?: number, currentMana?: number, exhaustionLevel?: number, hitDiceRemaining?: number }, resourceExpected?: { currentHp?: number, tempHp?: number, currentMana?: number, exhaustionLevel?: number, hitDiceRemaining?: number }, audit?: Record<string, any> }) => Promise<any>) | undefined} [patchCharacterSystemStateAndResources]
 *   Slice 8 authority seam for DM system-rule writes that touch only the
 *   system-state fields and explicit character rule-resource fields. Owns a
 *   single audit event.
 * @property {((args: { charId: string, systemUpdates?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, systemExpected?: { systemList?: any[], removedSystems?: any[], glowingSystems?: Record<string, any> }, effectUpdates?: { buffList?: any[], debuffList?: any[], permanentBuffs?: any[], permanentDebuffs?: any[], buffSources?: Record<string, any>, concentratingOn?: any, concentrationEffectGroupId?: any }, effectExpected?: { buffList?: any[], debuffList?: any[], permanentBuffs?: any[], permanentDebuffs?: any[], buffSources?: Record<string, any>, concentratingOn?: any, concentrationEffectGroupId?: any }, audit?: Record<string, any> }) => Promise<any>) | undefined} [patchCharacterSystemStateAndEffects]
 *   Slice 8 authority seam for DM system-rule writes that touch only the
 *   system-state fields plus legacy effect and concentration fields. Owns a
 *   single audit event.
 * @property {((args: { charId: string, itemEntryId: string, quantity?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [depositPartyInventoryFromChar]
 *   Session 4 authority seam. When implemented by the active backend, executes
 *   one authoritative `char.inventory -> partyInventory.items` deposit mutation
 *   and returns the server result. Missing on backends that still use the
 *   legacy client-composed transfer path.
 * @property {((args: { charId: string, itemEntryId: string, quantity?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [takePartyInventoryToChar]
 *   Session 4 authority seam. When implemented by the active backend, executes
 *   one authoritative `partyInventory.items -> char.inventory` take mutation
 *   and returns the server result. Missing on backends that still use the
 *   legacy client-composed transfer path.
 * @property {((args: { sourceCharId: string, targetCharId: string, itemEntryId: string, quantity?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [transferInventoryItemBetweenChars]
 *   Session 4 authority seam. When implemented by the active backend, executes
 *   one authoritative `char.inventory -> otherChar.inventory` transfer mutation
 *   and returns the server result. Missing on backends that still use the
 *   legacy client-composed give flow.
 * @property {((args: { charId: string, itemEntryId: string, quantity?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [moveCharItemToCraftingTable]
 *   Session 6 authority seam for `char.inventory -> char.craftingTable`.
 * @property {((args: { charId: string, item: any }) => Promise<any>) | undefined} [addInventoryItem]
 *   Session 12 authority seam for `InventoryPanelV2` item creation. When
 *   implemented by the active backend, owns appending one concrete inventory
 *   entry and its audit row.
 * @property {((args: { charId: string, itemEntryId: string, updates: any, idempotencyKey?: string }) => Promise<any>) | undefined} [updateInventoryItem]
 *   Session 12 authority seam for `InventoryPanelV2` item editor saves. When
 *   implemented by the active backend, owns sanitized item updates and audit.
 * @property {((args: { charId: string, itemEntryId: string, idempotencyKey?: string }) => Promise<any>) | undefined} [removeInventoryItem]
 *   Session 12 authority seam for `InventoryPanelV2` item deletion. Backend
 *   semantics preserve the equipped-delete guard.
 * @property {((args: { charId: string, itemEntryId: string, favorite: boolean, expectedFavorited?: boolean | null, idempotencyKey?: string }) => Promise<any>) | undefined} [setInventoryItemFavorite]
 *   Session 12 authority seam for target-state favorite/unfavorite updates.
 * @property {((args: { charId: string, itemEntryId: string, expectedEquipped: boolean, idempotencyKey?: string }) => Promise<any>) | undefined} [toggleInventoryItemEquipped]
 *   Session 12 authority seam for target-state `char.inventory[].equipped`
 *   updates. When implemented by the active backend, owns equip/unequip and
 *   associated item-effect provenance in one authoritative character write.
 * @property {((args: { charId: string, itemEntryId: string, expectedQuantity?: number, expectedUsesCurrent?: number | null, idempotencyKey?: string }) => Promise<any>) | undefined} [consumeInventoryItem]
 *   Session 12 authority seam for `InventoryPanelV2` consumable use. When
 *   implemented by the active backend, owns resource deltas, status-effect
 *   provenance, uses/quantity decrement, and receipt projection in one
 *   authoritative character write.
 * @property {((args: { charId: string, itemEntryId: string, itemDefinitionId?: string | null, abilityId: string, expectedUsesCurrent?: number | null, expectedUsesMax?: number | null, idempotencyKey?: string }) => Promise<any>) | undefined} [activateInventoryItemAbility]
 *   Session 12 authority seam for `InventoryPanelV2` non-combat item ability
 *   activation. When implemented by the active backend, owns ability use
 *   decrement, item-ability status provenance, receipt projection, and audit
 *   in one authoritative character write.
 * @property {((args: { charId: string, recipeResult: any, quantity: number, consumedIngredients: Array<{itemId: string, quantity: number}>, idempotencyKey?: string }) => Promise<any>) | undefined} [craftOutputToChar]
 *   Session 6 authority seam for `craft output -> char.inventory`.
 * @property {((args: { charId: string, recipeResult: any, quantity: number, consumedIngredients: Array<{itemId: string, quantity: number}>, idempotencyKey?: string }) => Promise<any>) | undefined} [craftOutputToParty]
 *   Session 6 authority seam for `craft output -> partyInventory.items`.
 * @property {((args: { charId: string, tableItemId: string, quantity?: number, fallbackResult?: any, idempotencyKey?: string }) => Promise<any>) | undefined} [tableRowToChar]
 *   Session 6 authority seam for `char.craftingTable -> char.inventory`.
 * @property {((args: { charId: string, tableItemId: string, quantity?: number, fallbackResult?: any, idempotencyKey?: string }) => Promise<any>) | undefined} [tableRowToParty]
 *   Session 6 authority seam for `char.craftingTable -> partyInventory.items`.
 * @property {((args: { charId: string, combatId?: string | null, itemEntryId: string, itemName?: string, abilityId: string, abilityName?: string, targetId?: string | null, expectedTargetType?: string | null, isCrit?: boolean | null, expectedAbilityUsesCurrent?: number | null, expectedAbilityUsesMax?: number | null, expectedCombatRound?: number | null, expectedCombatTurnIndex?: number | null, expectedTargetCurrentHp?: number | null, expectedTargetTempHp?: number | null, nonlethalOnly?: boolean | null, saveAbility?: string | null, saveDC?: number | null, saveEffect?: string | null, saveTotal?: number | null, savePassed?: boolean | null, activationSurface?: string, idempotencyKey?: string }) => Promise<any>) | undefined} [activateCombatItemAbility]
 *   Session 11 combat item-ability authority seam. When implemented by the
 *   active backend, owns the full item ability activation transaction for
 *   supported combat actions. Missing on backends that still use the legacy
 *   client-composed `CombatPage` runtime path.
 * @property {((args: { effectInstanceId: string, action: 'disable'|'enable'|'archive'|'hide'|'unhide', expectedLegacyKey?: string | null, expectedStatus?: 'active'|'disabled' | null }) => Promise<any>) | undefined} [applyEffectInstanceLifecycle]
 *   Session 11 status effect-instance lifecycle authority seam. When
 *   implemented by the active backend, owns durable disable/enable/archive and
 *   backend hide/unhide proof actions for server-created item-ability status
 *   rows. Missing on backends that still use the legacy client-composed
 *   status-effect path.
 * @property {((args: { charId: string, dryRun?: boolean, confirm?: string }) => Promise<any>) | undefined} [reconcileItemAbilityEffectInstancesForChar]
 *   Session 11 one-character durable item-ability effect-instance reconcile
 *   seam. When implemented by the active backend, backfills/patches/tombstones
 *   durable rows for the requested character. Intended for DM/admin tooling,
 *   with dry-run support before applying writes.
 * @property {((args: { charId: string, shopId: string, listingId: string, quantity: number, expectedPrice?: number, expectedListingVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [purchaseShopListing]
 *   Session 7 shop authority seam for one listing purchase.
 * @property {((args: { charId: string, shopId: string, lines: Array<{listingId: string, quantity: number, expectedPrice?: number, expectedListingVersion?: number}>, idempotencyKey?: string }) => Promise<any>) | undefined} [checkoutShopCart]
 *   Session 7 shop authority seam for multi-line cart checkout.
 * @property {((args: { charId: string, shopId: string, itemEntryId: string, quantity: number, payoutBase: number, expectedShopVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [sellInventoryItemToShopForDm]
 *   Session 9 shop trade authority seam for DM-approved sell-back from a concrete character inventory entry.
 * @property {((args: { charId: string, shopId: string, offeredItems: Array<{itemEntryId: string, quantity: number}>, offeredGoldBase?: number, requestedListings: Array<{listingId: string, quantity: number, expectedListingVersion: number, expectedPrice?: number}>, requestedGoldBase?: number, note?: string, expectedShopVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [submitTradeOffer]
 *   Session 9 shop trade authority seam for creating server-owned trade offers without settlement.
 * @property {((args: { offerId: string, expectedOfferVersion: number, idempotencyKey?: string, reason?: string }) => Promise<any>) | undefined} [cancelTradeOffer]
 *   Session 9 shop trade authority seam for cancelling pending server-owned trade offers without settlement.
 * @property {((args: { offerId: string, expectedOfferVersion: number, idempotencyKey?: string, reason?: string }) => Promise<any>) | undefined} [denyTradeOfferForDm]
 *   Session 9 shop trade authority seam for DM denial of pending server-owned trade offers without settlement.
 * @property {((args: { offerId: string, expectedOfferVersion: number, idempotencyKey?: string }) => Promise<any>) | undefined} [settleTradeOfferForDm]
 *   Session 9 shop trade authority seam for DM settlement of pending server-owned trade offers.
 * @property {((args: { transactionId: string, lineIndex: number, priceBase: number, secrecy?: string, tradeOnly?: boolean, idempotencyKey?: string }) => Promise<any>) | undefined} [promoteTradeReceiptLineToShopListingForDm]
 *   Session 9 shop trade authority seam for explicitly promoting a settled offered-item receipt line into a shop listing.
 * @property {((args: { shopId: string, deltaBase: number, reason?: string, expectedWalletVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [adjustShopWalletForDm]
 *   Session 9 shop wallet authority seam for DM-only manual shop-wallet ledger adjustments.
 * @property {((args: { shopData: any, dryRun?: boolean, confirm?: string }) => Promise<any>) | undefined} [importShopDataForDm]
 *   Session 7 shop runtime import/backfill seam. DM-only on Convex; missing on local/unsupported backends.
 * @property {((args: { clientShopId: string, activate?: boolean, patch: any }) => Promise<any>) | undefined} [createDmShop]
 *   Session 7 DM shop management seam for creating server-owned shop rows with deterministic runtime ids.
 * @property {((args: { shopId: string, expectedVersion?: number, replacementActiveShopId?: string | null }) => Promise<any>) | undefined} [deleteDmShop]
 *   Session 7 DM shop management seam for soft-deleting server-owned shop rows.
 * @property {((args: { shopId: string, expectedVersion?: number, patch: any }) => Promise<any>) | undefined} [updateDmShop]
 *   Session 7 DM shop management seam for existing server-owned shop rows.
 * @property {((args: { shopId: string, clientListingId: string, definitionId: string, templateSnapshot?: any, patch: any }) => Promise<any>) | undefined} [createDmShopListing]
 *   Session 7 DM shop management seam for creating server-owned listing rows with deterministic runtime ids.
 * @property {((args: { listingId: string, expectedVersion?: number }) => Promise<any>) | undefined} [deleteDmShopListing]
 *   Session 7 DM shop management seam for soft-deleting server-owned listing rows.
 * @property {((args: { listingId: string, expectedVersion?: number, patch: any, templateSnapshot?: any }) => Promise<any>) | undefined} [updateDmShopListing]
 *   Session 7 DM shop management seam for existing server-owned listing rows.
 * @property {((args: { expectedUpdatedAt?: number, patch: any }) => Promise<any>) | undefined} [updateDmShopRuntimeState]
 *   Session 7 DM shop management seam for server-owned shop runtime selection and settings state.
 * @property {((args: { encounterId: string, enemyId?: string | null, enemyName?: string, xpTotal?: number, drops: Array<{dropId: string, definitionId: string, quantity?: number, visibleToPlayers?: boolean, snapshot?: any, lootTableRowIndex?: number}>, expectedEncounterVersion?: number }) => Promise<any>) | undefined} [publishLootEncounterForDm]
 *   Session 8 loot runtime seam for creating/updating a DM-owned encounter and durable drop rows.
 * @property {((args: { lootData: any, dryRun?: boolean, confirm?: string }) => Promise<any>) | undefined} [importLootDataForDm]
 *   Session 8 loot runtime import/backfill seam. DM-only on Convex; imports active local loot encounter rows without replaying awards.
 * @property {((args: { encounterId: string, dropId: string, expectedDropVersion?: number }) => Promise<any>) | undefined} [claimLootDrop]
 *   Session 8 loot runtime seam for a player claiming one visible unawarded drop.
 * @property {((args: { encounterId: string, dropId: string, expectedDropVersion?: number }) => Promise<any>) | undefined} [withdrawLootClaim]
 *   Session 8 loot runtime seam for a player withdrawing their active claim on one drop.
 * @property {((args: { encounterId: string, dropId: string, visible: boolean, expectedDropVersion?: number }) => Promise<any>) | undefined} [setLootDropVisibilityForDm]
 *   Session 8 loot runtime seam for DM reveal/hide of one server-owned drop.
 * @property {((args: { encounterId: string, expectedEncounterVersion?: number }) => Promise<any>) | undefined} [revealAllLootDropsForDm]
 *   Session 8 loot runtime seam for DM reveal-all on a server-owned encounter.
 * @property {((args: { encounterId: string, expectedEncounterVersion?: number }) => Promise<any>) | undefined} [finishLootEncounterForDm]
 *   Session 8 loot runtime seam for ending a server-owned encounter without writing local loot state.
 * @property {((args: { encounterId: string, expectedEncounterVersion?: number }) => Promise<any>) | undefined} [distributeLootXpForDm]
 *   Session 8 loot runtime seam for DM authoritative XP distribution into character rows.
 * @property {((args: { encounterId: string, dropId: string, targetCharId: string, expectedDropVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [awardLootDrop]
 *   Session 8 loot runtime seam for DM authoritative non-currency item award into character inventory.
 * @property {((args: { encounterId: string, dropId: string, targetCharId: string, expectedDropVersion?: number, idempotencyKey?: string }) => Promise<any>) | undefined} [awardCurrencyLoot]
 *   Session 8 loot runtime seam for DM authoritative currency award into character wallet.
 * @property {() => void} flushPending
 *   Force any pending debounced writes to land synchronously. Called on tab close,
 *   before atomic updates, and from UX flows that need a persistence guarantee.
 * @property {() => void} dispose
 *   Tear down listeners + pending timers. Safe to call multiple times. After
 *   dispose, subsequent `subscribe` calls re-establish listeners.
 * @property {() => ConnectionStatus} getConnectionStatus
 *   Current connection status, polled by the hook at a low frequency.
 */

// The authoritative list of top-level collection paths. Every driver subscribes to
// these; every save* method in useStorage routes to one of them.
export const COLLECTION_PATHS = /** @type {CollectionPath[]} */ ([
  'chars', 'food', 'diets', 'diseases', 'systems',
  'foodTypes', 'locations', 'globalSettings', 'spellLibrary',
  'groupTravelLog', 'currentLocation', 'messages',
  'journal', 'dmJournal', 'npcLibrary', 'journalSettings',
  'combat', 'combatSnapshots', 'audit',
  // 2026-04-23: party shared inventory. Singleton-blob pattern like food/combat.
  'partyInventory',
  // 2026-05-05: server-owned shop runtime read projection.
  'shopRuntime',
  // 2026-05-05: server-owned loot runtime read projection.
  'lootRuntime',
]);

const COLLECTION_PATHS_SET = new Set(COLLECTION_PATHS);

// Read-only pseudo paths used by bounded Convex subscriptions. They are not
// writable top-level collections, so keep them out of COLLECTION_PATHS and the
// save/routing loops that assume a collection can be replaced.
export const VIRTUAL_SUBSCRIPTION_PATHS = /** @type {SubscriptionPath[]} */ ([
  'auditSessionMeta',
  'auditMeta',
  'auditRecent',
  'messageLiveTail',
  'messagePanelHistory',
  'messageUnreadCounts',
  'journalRecent',
  'journalPage',
  'journalEntry',
  'dmJournalRecent',
  'dmJournalPage',
  'dmJournalEntry',
]);

export const SUBSCRIPTION_PATHS = /** @type {SubscriptionPath[]} */ ([
  ...COLLECTION_PATHS,
  ...VIRTUAL_SUBSCRIPTION_PATHS,
]);

const SUBSCRIPTION_PATHS_SET = new Set(SUBSCRIPTION_PATHS);

export const buildEmptyConnectedShopRuntime = () => ({
  _source: 'convex-shop-runtime',
  activeShopId: null,
  activeCampaign: 'all',
  campaigns: [],
  offers: [],
  transactionLog: [],
  itemTemplates: {},
  shops: [],
});

export const buildEmptyConnectedLootRuntime = () => ({
  _source: 'convex-loot-runtime',
  items: {},
  enemies: [],
  activeEncounter: null,
  log: [],
});

// --- Shared helpers ---

/**
 * Apply a multi-path patch to a nested object. Keys are slash-separated paths like
 * `"audit/events/xyz"` or `"chars/abc/hp"`. Missing intermediate objects are created.
 * Pure: returns a new object with structural sharing for untouched branches.
 *
 * Used by LocalDriver for local state updates and by FirebaseDriver for optimistic
 * cache updates ahead of network writes. Contract matches the original inline
 * `applyPatchLocal` in App.js so the optimistic-write semantics are preserved.
 *
 * @param {any} base
 * @param {Record<string, any>} patch
 */
export const applyPatchLocal = (base, patch) => {
  const cloned = { ...base };
  const setByPath = (obj, path, value) => {
    const parts = (path || '').split('/').filter(Boolean);
    if (parts.length === 0) return;
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      const next = cur[k];
      if (typeof next !== 'object' || next === null) {
        cur[k] = {};
      } else {
        cur[k] = Array.isArray(next) ? [...next] : { ...next };
      }
      cur = cur[k];
    }
    cur[parts[parts.length - 1]] = value;
  };
  Object.entries(patch || {}).forEach(([p, v]) => setByPath(cloned, p, v));
  return cloned;
};

/**
 * Build a per-collection normalizer map. Each normalizer takes a raw value from
 * the source backend and applies the collection's fallback/default. Same shapes
 * the original useStorage used inline, factored out so every driver can share.
 *
 * @param {{ defaults: Object, demoData?: Object }} config
 */
export const buildNormalizers = ({ defaults, demoData }) => ({
  chars: v => v || {},
  food: v => v || (demoData?.food ?? { totalUnits: 0, foodLog: [] }),
  diets: v => v || defaults.diets,
  diseases: v => v || defaults.diseases,
  systems: v => v || defaults.systems,
  foodTypes: v => v || defaults.foodTypes,
  locations: v => v || defaults.locations,
  globalSettings: v => v || defaults.globalSettings,
  spellLibrary: v => v || [],
  groupTravelLog: v => v || [],
  // Normalizer fallback is 'Forest' (defensive), while the initial state below uses
  // 'Stone Bridge' (demo start). Divergence is intentional — preserved from original.
  currentLocation: v => v || 'Forest',
  messages: v => v || { statusUpdates: [], dmChats: {} },
  journal: v => (Array.isArray(v) ? v : (v ? Object.values(v) : defaults.journal || [])).filter(Boolean),
  dmJournal: v => Array.isArray(v) ? v : (v ? Object.values(v) : []),
  npcLibrary: v => v || defaults.npcLibrary,
  journalSettings: v => v || defaults.journalSettings || { showLinkTooltips: true },
  combat: v => v || (demoData?.combat ?? { active: false, combatants: [], enemies: [], round: 1, turnIndex: 0, damageLog: [] }),
  combatSnapshots: v => Array.isArray(v) ? v : (v ? Object.values(v) : []),
  audit: v => v || defaults.audit,
  auditSessionMeta: v => v || { activeSessionId: null, sessions: {} },
  auditMeta: v => v || { activeSessionId: null, sessions: {}, snapshots: {} },
  auditRecent: v => v || { events: {} },
  messageLiveTail: v => v || { statusUpdates: [], dmChats: {} },
  messagePanelHistory: v => v || { statusUpdates: [], dmChats: {} },
  messageUnreadCounts: v => v || { statusUnreadCount: 0, dmUnreadCount: 0 },
  journalRecent: v => Array.isArray(v) ? v : [],
  journalPage: v => v || { page: [], isDone: true, continueCursor: null },
  journalEntry: v => v || null,
  dmJournalRecent: v => Array.isArray(v) ? v : [],
  dmJournalPage: v => v || { page: [], isDone: true, continueCursor: null },
  dmJournalEntry: v => v || null,
  // 2026-04-23: party shared inventory. Default is an empty shared pool.
  partyInventory: v => v || { items: [] },
  // Null means App may keep using browser-local data in explicit local/legacy mode.
  // Campaign sandboxes may seed local runtime snapshots here; those stay inside
  // the sandbox storage envelope rather than the legacy browser-local keys.
  shopRuntime: v => v || defaults.shopRuntime || null,
  lootRuntime: v => v || defaults.lootRuntime || null,
});

/**
 * Build the initial data object used as the React state seed AND as the Firebase
 * one-shot seed payload. Same shape the original useStorage used inline.
 *
 * @param {{ defaults: Object, demoData?: Object, useServerDerivedViewerQueries?: boolean }} config
 */
export const buildInitialData = ({ defaults, demoData, useServerDerivedViewerQueries = false }) => {
  const shouldSeedDemoData = !useServerDerivedViewerQueries;
  return {
    chars: shouldSeedDemoData ? (demoData?.chars ?? {}) : {},
    food: shouldSeedDemoData ? (demoData?.food ?? { totalUnits: 0, foodLog: [] }) : { totalUnits: 0, foodLog: [] },
    diets: defaults.diets,
    diseases: defaults.diseases,
    systems: defaults.systems,
    foodTypes: defaults.foodTypes,
    locations: defaults.locations,
    globalSettings: defaults.globalSettings,
    spellLibrary: [],
    groupTravelLog: shouldSeedDemoData ? (demoData?.groupTravelLog ?? []) : [],
    currentLocation: defaults.currentLocation ?? 'Stone Bridge',
    messages: { statusUpdates: [], dmChats: {} },
    journal: defaults.journal ?? [],
    dmJournal: [],
    npcLibrary: defaults.npcLibrary,
    journalSettings: defaults.journalSettings || { showLinkTooltips: true },
    combat: shouldSeedDemoData ? (demoData?.combat ?? { active: false, combatants: [], enemies: [], round: 1, turnIndex: 0, damageLog: [] }) : { active: false, combatants: [], enemies: [], round: 1, turnIndex: 0, damageLog: [] },
    combatSnapshots: [],
    audit: defaults.audit,
    auditSessionMeta: { activeSessionId: null, sessions: {} },
    auditMeta: { activeSessionId: null, sessions: {}, snapshots: {} },
    auditRecent: { events: {} },
    messageLiveTail: { statusUpdates: [], dmChats: {} },
    messagePanelHistory: { statusUpdates: [], dmChats: {} },
    messageUnreadCounts: { statusUnreadCount: 0, dmUnreadCount: 0 },
    journalRecent: [],
    journalPage: { page: [], isDone: true, continueCursor: null },
    journalEntry: null,
    dmJournalRecent: [],
    dmJournalPage: { page: [], isDone: true, continueCursor: null },
    dmJournalEntry: null,
    partyInventory: { items: [] },
    shopRuntime: useServerDerivedViewerQueries ? buildEmptyConnectedShopRuntime() : (defaults.shopRuntime || null),
    lootRuntime: useServerDerivedViewerQueries ? buildEmptyConnectedLootRuntime() : (defaults.lootRuntime || null),
  };
};

// --- Internal helpers ---

export const isCollectionPath = (p) => COLLECTION_PATHS_SET.has(p);

export const isSubscriptionPath = (p) => SUBSCRIPTION_PATHS_SET.has(p);

// Generate a unique id for local-only mode. Firebase driver uses `ref().push().key`
// (globally unique via Firebase's push-id scheme); this is the offline substitute.
// Includes enough entropy for audit event collisions to be vanishingly rare inside
// a single session; collisions across sessions don't matter since sessions are scoped.
export const generateLocalId = () =>
  `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const isTruthyLocalSandboxParam = (value) => (
  value === ''
  || value === '1'
  || value === 'true'
  || value === 'yes'
  || value === 'demo'
  || value === 'blank'
);

// Check whether the local mode should be forced (dev escape hatch: `?local=1`,
// campaign sandbox direct launches, or window.__LOCAL_ONLY__). Matches the
// original behavior in useStorage init.
export const shouldForceLocalMode = () => {
  if (typeof window === 'undefined') return false;
  if (window.__LOCAL_ONLY__ === true) return true;
  try {
    const params = new URLSearchParams(window.location?.search || '');
    if (params.has('local')) return true;
    if (!params.has('campaignSandbox')) return false;
    return isTruthyLocalSandboxParam(params.get('campaignSandbox') || '');
  } catch {
    return false;
  }
};
